import {
    isCancelledError,
    MutationCache,
    QueryCache,
    QueryClient,
    type DefaultError,
    type EnsureQueryDataOptions,
    type QueryKey,
} from '@tanstack/react-query';

import { ApiContractError, ApiError, apiErrorMessageKey } from './api';
import { isHttpError } from './errors';
import { trackEvent } from './trackingEvents';
import { rollbarClient } from './useRollbar';

const EXPECTED_HTTP_STATUSES = new Set([401, 403, 404, 422]);

// Error codes the backend returns once the partner-cockpit session is no longer valid.
const SESSION_ENDED_ERROR_CODES = new Set([
    'TOKEN_MISSING',
    'USER_DELETED',
    'NO_PARTNER_MEMBERSHIP_OR_ONBOARDING_APPLICATION',
]);

// Mutation failures that should surface a translated snackbar instead of only logging to Rollbar.
const USER_FACING_MUTATION_ERROR_CODES = new Set(['IMPERSONATION_READ_ONLY']);

export const STALE_TIME_MS = 5 * 60_000;

// Every settings save is keyed `['settings', 'save' | 'team', <what>]`, so one hook here counts them all without each
// form having to remember to. Verification is left out: it has events of its own, and counting its uploads here would
// mix it into "settings changed after checkout".
function trackSettingsChange(mutationKey: QueryKey | undefined): void {
    const [area, kind, setting] = mutationKey ?? [];
    if (area !== 'settings' || (kind !== 'save' && kind !== 'team') || typeof setting !== 'string') {
        return;
    }
    if (setting === 'validation') {
        return;
    }
    trackEvent('Settings Changed', { setting: kind === 'team' ? 'team' : setting });
}

// React Query cancels an in-flight fetch when its last observer unmounts, so any redirect — or a StrictMode
// remount — that tears a route down mid-load rejects the loader's `ensureQueryData` with `CancelledError`.
// That is not an answer about the request, and route guards that only handle `ApiError` let it reach the
// router's error boundary as a dead-end error screen. Ask again instead: the retry either resolves, or fails
// with the real error.
export async function retryIfCancelled<T>(load: () => Promise<T>): Promise<T> {
    try {
        return await load();
    } catch (error) {
        if (!isCancelledError(error)) {
            throw error;
        }
        return await load();
    }
}

export function isExpectedQueryError(error: unknown): boolean {
    return isHttpError(error) && EXPECTED_HTTP_STATUSES.has(error.status);
}
const isExpectedHttpError = isExpectedQueryError;

let onSessionEnded: ((error: ApiError) => void) | undefined;

export function setSessionEndedHandler(handler: (error: ApiError) => void): void {
    onSessionEnded = handler;
}

export function isSessionEndedError(error: unknown): error is ApiError {
    return error instanceof ApiError && error.errorCode !== undefined && SESSION_ENDED_ERROR_CODES.has(error.errorCode);
}

export function isNoPartnerMembershipOrOnboardingApplicationError(error: unknown): boolean {
    return error instanceof ApiError && error.errorCode === 'NO_PARTNER_MEMBERSHIP_OR_ONBOARDING_APPLICATION';
}

function handleSessionEnded(error: unknown): void {
    if (isSessionEndedError(error)) {
        onSessionEnded?.(error);
    }
}

let onUserFacingMutationError: ((messageKey: string) => void) | undefined;

export function setUserFacingMutationErrorHandler(handler: ((messageKey: string) => void) | undefined): void {
    onUserFacingMutationError = handler;
}

export function isUserFacingMutationError(error: unknown): boolean {
    return (
        error instanceof ApiError &&
        error.errorCode !== undefined &&
        USER_FACING_MUTATION_ERROR_CODES.has(error.errorCode)
    );
}

function handleUserFacingMutationError(error: unknown): void {
    if (isUserFacingMutationError(error)) {
        onUserFacingMutationError?.(apiErrorMessageKey(error, 'ERRORS.GENERIC.DESCRIPTION'));
    }
}

export function notifyUserFacingMutationError(error: unknown): void {
    handleUserFacingMutationError(error);
}

function reportToRollbar(label: string, error: unknown): void {
    if (isExpectedHttpError(error)) {
        return;
    }
    rollbarClient.error(label, error instanceof Error ? error : new Error(String(error)));
}

// Every route loader goes through `ensureQueryData`, so the retry lives on the client rather than at each call
// site — a route added later gets it without anyone having to remember.
class CockpitQueryClient extends QueryClient {
    public override async ensureQueryData<
        TQueryFnData,
        TError = DefaultError,
        TData = TQueryFnData,
        TQueryKey extends QueryKey = QueryKey,
    >(options: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>): Promise<TData> {
        return retryIfCancelled(() => super.ensureQueryData(options));
    }
}

export const queryClient = new CockpitQueryClient({
    // Fires for every failed query (read), regardless of whether the call site
    // attached an onError.
    queryCache: new QueryCache({
        onError: (error: unknown): void => {
            handleSessionEnded(error);
            reportToRollbar('Unexpected query error', error);
        },
    }),
    // Same idea for mutations (writes). Without this, a useMutation call that
    // doesn't pass its own onError will fail silently to the console.
    mutationCache: new MutationCache({
        onSuccess: (_data, _variables, _context, mutation): void => {
            trackSettingsChange(mutation.options.mutationKey);
        },
        onError: (error: unknown): void => {
            handleSessionEnded(error);
            handleUserFacingMutationError(error);
            reportToRollbar('Unexpected mutation error', error);
        },
    }),
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60,
            gcTime: 1000 * 60 * 5, // 5 minutes
            // Should not retry on schema/contract violations
            retry: (failureCount: number, error: unknown): boolean =>
                !(error instanceof ApiContractError) && !isSessionEndedError(error) && failureCount < 1,
            refetchOnWindowFocus: false,
        },
        mutations: {
            // Library default is already 0, but pin it explicitly because mutations
            // aren't idempotent.
            retry: 0,
        },
    },
});
